/**
 * SSE (Server-Sent Events) Transport Implementation.
 *
 * Provides HTTP-based streaming using Server-Sent Events format.
 * This is the default/fallback transport when WebSocket is unavailable.
 */

import type {
  ChatTransport,
  ChatTransportConfig,
  SendMessagesRequest,
  ReconnectRequest,
  StreamEvent,
  TransportConnectionState,
} from './ChatTransport';
import { resolveHeaders } from './ChatTransport';

/**
 * SSE Transport configuration.
 */
export interface SSETransportConfig extends ChatTransportConfig {
  /** Chat endpoint path (default: '/chat') */
  chatEndpoint?: string;
  /** Stop endpoint path (default: '/chat/stop') */
  stopEndpoint?: string;
}

/**
 * SSE-based chat transport.
 *
 * Sends messages via HTTP POST and streams responses using
 * Server-Sent Events format (data: {json}\n).
 */
export class SSETransport implements ChatTransport {
  private config: SSETransportConfig;
  private abortController: AbortController | null = null;

  constructor(config: SSETransportConfig) {
    this.config = {
      chatEndpoint: '/chat',
      stopEndpoint: '/chat/stop',
      ...config,
    };
  }

  /**
   * Send messages and stream responses.
   */
  async *sendMessages(request: SendMessagesRequest): AsyncIterable<StreamEvent> {
    const { chatId, userId, organizationId, message, abortSignal, headers, body } = request;

    // Abort any previous stream before starting a new one
    this.abortController?.abort();
    this.abortController = new AbortController();
    if (abortSignal) {
      abortSignal.addEventListener('abort', () => this.abortController?.abort(), { once: true });
    }

    const url = `${this.config.baseUrl}${this.config.chatEndpoint}`;
    const requestHeaders = resolveHeaders(this.config, {
      'Content-Type': 'application/json',
      ...headers,
    });

    let response: Response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: requestHeaders,
        credentials: this.config.credentials,
        body: JSON.stringify({
          session_id: chatId,
          user_id: userId,
          ...(organizationId && { organization_id: organizationId }),
          message,
          ...body,
        }),
        signal: this.abortController.signal,
      });
    } catch (error) {
      // Clean up abort controller on fetch error
      this.abortController = null;
      throw error;
    }

    if (!response.ok) {
      this.abortController = null;

      // Handle auth failure: clear stale token and redirect to login
      if (response.status === 401) {
        localStorage.removeItem('cxs_token');
        localStorage.removeItem('cxs_user');
        window.location.href = '/login?error=session_expired';
        throw new Error('Session expired — redirecting to sign in');
      }

      let msg = `HTTP ${response.status}: ${response.statusText}`;
      if (
        response.status === 404 &&
        this.config.chatEndpoint?.includes('/agents/')
      ) {
        msg +=
          ' — If port 8090 runs a single agent (e.g. `./utils/devctl.sh agent start tychi`) ' +
          'and not the orchestrator, set REACT_APP_SINGLE_AGENT_SSE=true in `shared/frontend/basic/.env` ' +
          'and restart the frontend dev server.';
      }
      throw new Error(msg);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      this.abortController = null;
      throw new Error('No response stream available');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');

        // Keep the last potentially incomplete line in buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;

          const payload = line.slice(6);
          if (payload === '[DONE]') {
            return;
          }

          try {
            const event = JSON.parse(payload) as StreamEvent;
            yield event;

            // Only close on explicit error. Do NOT close on 'finish'
            // events — they can be intermediate (between tool calls
            // during multi-step delegations). The stream terminates
            // properly via [DONE] above.
            if (event.type === 'error') {
              return;
            }
          } catch (e) {
            console.warn('[SSE] Skipping malformed event:', payload.slice(0, 200), e);
            continue;
          }
        }
      }

      // Process any remaining buffer content
      if (buffer.startsWith('data: ')) {
        const payload = buffer.slice(6);
        if (payload !== '[DONE]') {
          try {
            yield JSON.parse(payload) as StreamEvent;
          } catch {
            // Ignore parse errors for trailing content
          }
        }
      }
    } finally {
      reader.releaseLock();
      this.abortController = null;
    }
  }

  /**
   * SSE transport doesn't support stream reconnection.
   */
  async reconnectToStream(_request: ReconnectRequest): Promise<AsyncIterable<StreamEvent> | null> {
    return null;
  }

  /**
   * Stop the current stream.
   */
  async stop(chatId: string, userId: string): Promise<void> {
    // Abort the current request
    this.abortController?.abort();

    // Send stop request to backend
    const url = `${this.config.baseUrl}${this.config.stopEndpoint}`;
    try {
      await fetch(url, {
        method: 'POST',
        headers: resolveHeaders(this.config, { 'Content-Type': 'application/json' }),
        credentials: this.config.credentials,
        body: JSON.stringify({
          session_id: chatId,
          user_id: userId,
        }),
      });
    } catch {
      // Ignore stop request failures
    }
  }

  /**
   * SSE is stateless - returns 'connected' only while actively streaming,
   * 'disconnected' otherwise.
   */
  getConnectionState(): TransportConnectionState {
    return this.abortController ? 'connected' : 'disconnected';
  }

  /**
   * Clean up resources.
   */
  dispose(): void {
    this.abortController?.abort();
    this.abortController = null;
  }
}
